"use client";

import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "@/store/store";
import { fetchMetafieldsRequest } from "@/store/slices/metafield.slice";
import {
  StandardFormInput,
  StandardFormSelect,
  StandardFormTagInput,
} from "@/components/common/StandardFormInput";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Type,
  Hash,
  List,
  Mail,
  Link as LinkIcon,
  Globe,
  Package,
} from "lucide-react";
import { Metafield as MetafieldModel } from "@/types/models/metafield";

interface MetafieldValue {
  key: string;
  type: string;
  value: string | number | string[];
}

interface ProductMetafieldsCardProps {
  values: MetafieldValue[];
  onChange: (values: MetafieldValue[]) => void;
  className?: string;
}

export const ProductMetafieldsCard: React.FC<ProductMetafieldsCardProps> = ({
  values,
  onChange,
  className = "",
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { items: availableMetafields, loading } = useSelector(
    (state: RootState) => state.metafields,
  );

  // Filter metafields for products only
  const productMetafields = availableMetafields.filter(
    (metafield) => metafield.applied_to === "products",
  );

  useEffect(() => {
    // Fetch metafields when component mounts
    if (availableMetafields.length === 0) {
      dispatch(fetchMetafieldsRequest({}));
    }
  }, [dispatch, availableMetafields.length]);

  const getTypeIcon = (type: string) => {
    const typeConfig: Record<string, { icon: React.ReactNode; color: string }> =
      {
        text: { icon: <Type className="h-4 w-4" />, color: "text-blue-600" },
        number: { icon: <Hash className="h-4 w-4" />, color: "text-green-600" },
        select: {
          icon: <List className="h-4 w-4" />,
          color: "text-purple-600",
        },
        email: { icon: <Mail className="h-4 w-4" />, color: "text-orange-600" },
        url: { icon: <Globe className="h-4 w-4" />, color: "text-cyan-600" },
        link: {
          icon: <LinkIcon className="h-4 w-4" />,
          color: "text-indigo-600",
        },
      };

    const cfg = typeConfig[type] || {
      icon: <Type className="h-4 w-4" />,
      color: "text-gray-600",
    };
    return <div className={cfg.color}>{cfg.icon}</div>;
  };

  const getTypeBadge = (type: string) => {
    const typeColors: Record<string, string> = {
      text: "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
      number: "bg-green-500/20 text-green-600 hover:bg-green-500/20",
      select: "bg-purple-500/20 text-purple-600 hover:bg-purple-500/20",
      email: "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20",
      url: "bg-cyan-500/20 text-cyan-600 hover:bg-cyan-500/20",
      link: "bg-indigo-500/20 text-indigo-600 hover:bg-indigo-500/20",
    };

    const cls = typeColors[type] || "bg-gray-200 text-gray-600";

    return (
      <Badge className={cls}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </Badge>
    );
  };

  const handleInputChange = (
    metafield: MetafieldModel,
    newValue: string | string[],
  ) => {
    const existingIndex = values.findIndex((v) => v.key === metafield.key);
    const updatedValue: MetafieldValue = {
      key: metafield.key,
      type: metafield.type,
      value: newValue,
    };

    if (existingIndex !== -1) {
      // Update existing value
      const updatedValues = [...values];
      updatedValues[existingIndex] = updatedValue;
      onChange(updatedValues);
    } else {
      // Add new value
      onChange([...values, updatedValue]);
    }
  };

  const getCurrentValue = (metafield: MetafieldModel): string | string[] => {
    const current = values.find((v) => v.key === metafield.key);
    if (!current) return metafield.type === "select" ? [] : "";

    // Handle select type which should be an array
    if (metafield.type === "select" && typeof current.value === "string") {
      return [current.value];
    }

    return current.value as string | string[];
  };

  const renderInputField = (metafield: MetafieldModel) => {
    const currentValue = getCurrentValue(metafield);

    switch (metafield.type) {
      case "text":
        return (
          <StandardFormInput
            label=""
            placeholder={`Enter ${metafield.key}`}
            value={currentValue as string}
            onChange={(e) => handleInputChange(metafield, e.target.value)}
            helperText={
              metafield.accepted_range && metafield.accepted_range.length === 2
                ? `Length: ${metafield.accepted_range[0]} - ${metafield.accepted_range[1]} characters`
                : undefined
            }
          />
        );

      case "number":
        return (
          <StandardFormInput
            label=""
            type="number"
            placeholder={`Enter ${metafield.key}`}
            value={currentValue as string}
            onChange={(e) => handleInputChange(metafield, e.target.value)}
            helperText={
              metafield.accepted_range && metafield.accepted_range.length === 2
                ? `Range: ${metafield.accepted_range[0]} - ${metafield.accepted_range[1]}`
                : undefined
            }
          />
        );

      case "email":
        return (
          <StandardFormInput
            label=""
            type="email"
            placeholder={`Enter email for ${metafield.key}`}
            value={currentValue as string}
            onChange={(e) => handleInputChange(metafield, e.target.value)}
          />
        );

      case "url":
      case "link":
        return (
          <StandardFormInput
            label=""
            type="url"
            placeholder={`Enter URL for ${metafield.key}`}
            value={currentValue as string}
            onChange={(e) => handleInputChange(metafield, e.target.value)}
          />
        );

      case "select":
        if (
          !metafield.accepted_values ||
          metafield.accepted_values.length === 0
        ) {
          return (
            <StandardFormTagInput
              label=""
              tags={Array.isArray(currentValue) ? currentValue : []}
              onTagsChange={(tags) => handleInputChange(metafield, tags)}
              placeholder={`Enter values for ${metafield.key}`}
            />
          );
        }

        return (
          <StandardFormSelect
            label=""
            value={
              Array.isArray(currentValue)
                ? currentValue[0] || ""
                : (currentValue as string)
            }
            onValueChange={(value) => handleInputChange(metafield, value)}
            options={metafield.accepted_values.map((val) => ({
              label: val,
              value: val,
            }))}
            placeholder={`Select ${metafield.key}`}
          />
        );

      default:
        return (
          <StandardFormInput
            label=""
            placeholder={`Enter ${metafield.key}`}
            value={currentValue as string}
            onChange={(e) => handleInputChange(metafield, e.target.value)}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <Label className="text-base font-semibold">Product Metafields</Label>
        </div>
        <div className="p-4 text-center text-muted-foreground">
          Loading metafields...
        </div>
      </div>
    );
  }

  if (productMetafields.length === 0) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <Label className="text-base font-semibold">Product Metafields</Label>
        </div>
        <div className="p-4 text-center text-muted-foreground">
          No product metafields found. Create metafields in Settings →
          Metafields first.
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <Package className="h-5 w-5 text-primary" />
        <Label className="text-base font-semibold">Product Metafields</Label>
        <Badge variant="outline" className="ml-2">
          {productMetafields.length} available
        </Badge>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column: Available Metafields */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b">
            <Label className="text-sm font-medium text-muted-foreground">
              Available Fields
            </Label>
          </div>

          <div className="space-y-2">
            {productMetafields.map((metafield) => (
              <div
                key={metafield.key}
                className="p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                {/* Compact two-row layout */}
                <div className="space-y-1">
                  {/* Row 1: Icon, Title, and Type Badge */}
                  <div className="flex items-center gap-2">
                    {getTypeIcon(metafield.type)}
                    <h4 className="font-medium text-sm capitalize flex-1">
                      {metafield.key.replace(/-/g, " ")}
                    </h4>
                    {getTypeBadge(metafield.type)}
                  </div>

                  {/* Row 2: Key */}
                  <div className="text-xs text-muted-foreground ml-6">
                    <code className="bg-muted px-1 py-0.5 rounded text-xs">
                      {metafield.key}
                    </code>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Input Fields */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b">
            <Label className="text-sm font-medium text-muted-foreground">
              Field Values
            </Label>
          </div>

          <div className="space-y-3">
            {productMetafields.map((metafield) => (
              <div key={metafield.key} className="space-y-1">
                <Label className="text-sm font-medium capitalize">
                  {metafield.key.replace(/-/g, " ")}
                </Label>
                {renderInputField(metafield)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
